/**
 * CAD Analysis Tool
 * LLM-powered CAD matching analysis with sub-tools for reports
 */
import React, { useState, useEffect, useRef, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  makeStyles,
  tokens,
  Text,
  Button,
  Input,
  Textarea,
  Card,
  CardHeader,
  Spinner,
  Tab,
  TabList,
  TabValue,
  SelectTabEvent,
  SelectTabData,
  Divider,
  Badge,
  Menu,
  MenuTrigger,
  MenuPopover,
  MenuList,
  MenuItem,
  Dialog,
  DialogTrigger,
  DialogSurface,
  DialogTitle,
  DialogBody,
  DialogActions,
  DialogContent,
  Checkbox,
  Field,
  Table,
  TableHeader,
  TableRow,
  TableHeaderCell,
  TableBody,
  TableCell,
  MessageBar,
  MessageBarBody,
  MessageBarTitle,
} from "@fluentui/react-components";
import {
  Send24Regular,
  Add24Regular,
  Delete24Regular,
  History24Regular,
  Chat24Regular,
  DocumentTable24Regular,
  PersonTag24Regular,
  VehicleCar24Regular,
  MoreHorizontal24Regular,
  Edit24Regular,
  Checkmark24Regular,
  Dismiss24Regular,
  ArrowSync24Regular,
  Clock24Regular,
} from "@fluentui/react-icons";

import useCadApiClient from "../../hooks/useCadApiClient";
import useUsageTracking from "../../hooks/useUsageTracking";
import { VoiceTextInputButton } from "../../components/VoiceTextInputButton";

// ============================================================================
// Types
// ============================================================================

interface Message {
  role: "user" | "assistant" | "system";
  content: string;
  timestamp: string;
}

interface Conversation {
  id: string;
  title: string;
  messages: Message[];
  message_count: number;
  created_at: string;
  updated_at: string;
}

interface ConversationSummary {
  id: string;
  title: string;
  message_count: number;
  created_at: string;
  updated_at: string;
  preview?: string;
}

interface CADPermissions {
  user_id: string;
  tool_id: string;
  permissions: string[];
  features: {
    chat: boolean;
    cad_matching: boolean;
    badge_mismatch: boolean;
    unit_vehicle: boolean;
    cad_catchup: boolean;
    tardy_ingestion: boolean;
  };
}

interface ReportOrganization {
  organization_id: number;
  organization_name: string;
  total_videos: number;
  matched_videos: number;
  overall_percent: number | null;
  bwc_total: number;
  bwc_matched: number;
  bwc_percent: number | null;
  icv_total: number;
  icv_matched: number;
  icv_percent: number | null;
  last_cad_parsed: string | null;
  has_issues: boolean;
  // Monitoring report fields (only present when no date range specified)
  fourteen_day_bwc_percent?: number | null;
  fourteen_day_icv_percent?: number | null;
  yesterday_bwc_percent?: number | null;
  yesterday_icv_percent?: number | null;
  window_adjusted?: string | null;
}

interface CADMatchingReportData {
  report_date: string;
  date_range: {
    start: string;
    end: string;
  };
  threshold: number;
  organization_count: number;
  organizations: ReportOrganization[];
  excluded_organizations: { id: number; name: string }[];
  email_sent?: boolean;
  email_message?: string;
  is_monitoring_report?: boolean;
}

interface BadgeMismatchRecord {
  organization_name: string;
  organization_id: number;
  officer_name: string;
  cad_badge: string | null;
  portal_badge: string | null;
  found_first_name: string | null;
  found_last_name: string | null;
  is_enabled: boolean | null;
  last_cad_activity: string | null;
  match_status: string;
}

interface BadgeMismatchReportData {
  report_date: string;
  lookback_days: number;
  portal_mismatches: BadgeMismatchRecord[];
  cad_data_issues: BadgeMismatchRecord[];
  disabled_recent_activity: BadgeMismatchRecord[];
  not_found: BadgeMismatchRecord[];
  summary: {
    portal_mismatch_count: number;
    cad_data_issue_count: number;
    disabled_recent_count: number;
    not_found_count: number;
    total_records: number;
  };
  email_sent?: boolean;
  email_message?: string;
  from_cache?: boolean;
  cache_age_seconds?: number;
}

// Unit/Vehicle report types
interface UnitVehicleOrgData {
  organization_id: number;
  organization_name: string;
  unmatched_units: string[];
  matched_units: string[];
  unmatched_vehicles: string[];
  matched_vehicles: string[];
}

interface UnitVehicleReportData {
  report_date: string;
  lookback_days: number;
  organizations: UnitVehicleOrgData[];
  summary: {
    organization_count: number;
    unmatched_units: number;
    unmatched_vehicles: number;
    matched_units: number;
    matched_vehicles: number;
    total_unmatched: number;
    total_matched: number;
  };
  email_sent?: boolean;
  email_message?: string;
}

// Tardy Ingestion report types
interface TardyIngestionOrg {
  organization_id: number;
  organization_name: string;
  most_recent_parsed_date: string;
  days_behind: number;
}

interface TardyIngestionReportData {
  report_date: string;
  threshold_days: number;
  min_days_behind: number;
  organization_count: number;
  organizations: TardyIngestionOrg[];
  excluded_organizations: { id: number | null; name: string }[];
  all_well: boolean;
  email_sent?: boolean;
  email_message?: string;
}

// ============================================================================
// Styles
// ============================================================================

const useStyles = makeStyles({
  container: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    backgroundColor: tokens.colorNeutralBackground2,
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  headerTitle: {
    display: "flex",
    alignItems: "center",
    gap: tokens.spacingHorizontalS,
  },
  headerIcon: {
    width: "32px",
    height: "32px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: "6px",
    color: tokens.colorNeutralForegroundOnBrand,
  },
  tabList: {
    padding: `0 ${tokens.spacingHorizontalL}`,
    backgroundColor: tokens.colorNeutralBackground1,
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  content: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
  },
  chatContainer: {
    flex: 1,
    display: "flex",
    overflow: "hidden",
  },
  sidebar: {
    width: "280px",
    borderRight: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
    display: "flex",
    flexDirection: "column",
  },
  sidebarHeader: {
    padding: tokens.spacingVerticalM,
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  conversationList: {
    flex: 1,
    overflowY: "auto",
    padding: tokens.spacingVerticalS,
  },
  conversationItem: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: tokens.spacingVerticalS,
    paddingLeft: tokens.spacingHorizontalM,
    paddingRight: tokens.spacingHorizontalS,
    borderRadius: "6px",
    cursor: "pointer",
    marginBottom: "4px",
    transition: "background-color 0.1s",
    ":hover": {
      backgroundColor: tokens.colorNeutralBackground1Hover,
    },
  },
  conversationItemActive: {
    backgroundColor: tokens.colorBrandBackground2,
    ":hover": {
      backgroundColor: tokens.colorBrandBackground2,
    },
  },
  conversationInfo: {
    flex: 1,
    overflow: "hidden",
  },
  conversationTitle: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "13px",
    fontWeight: 500,
  },
  conversationPreview: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    marginTop: "2px",
  },
  mainChat: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  messagesContainer: {
    flex: 1,
    overflowY: "auto",
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
  },
  message: {
    marginBottom: tokens.spacingVerticalM,
    maxWidth: "800px",
  },
  messageUser: {
    marginLeft: "auto",
  },
  messageBubble: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalL,
    paddingRight: tokens.spacingHorizontalL,
    borderRadius: "12px",
    whiteSpace: "pre-wrap",
    fontSize: "14px",
    lineHeight: "1.5",
  },
  messageBubbleUser: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    borderBottomRightRadius: "4px",
  },
  messageBubbleAssistant: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottomLeftRadius: "4px",
    "& table": {
      borderCollapse: "collapse" as const,
      width: "100%",
      margin: "8px 0 12px 0",
      fontSize: "13px",
    },
    "& th": {
      backgroundColor: tokens.colorNeutralBackground3,
      fontWeight: 600,
      padding: "6px 12px",
      textAlign: "left" as const,
      border: `1px solid ${tokens.colorNeutralStroke1}`,
    },
    "& td": {
      padding: "5px 12px",
      border: `1px solid ${tokens.colorNeutralStroke1}`,
      verticalAlign: "top",
    },
    "& tr:nth-child(even) td": {
      backgroundColor: tokens.colorNeutralBackground2,
    },
    "& p": {
      margin: "4px 0",
    },
  },
  messageLabel: {
    fontSize: "11px",
    color: tokens.colorNeutralForeground3,
    marginBottom: "4px",
  },
  inputContainer: {
    padding: tokens.spacingVerticalM,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    borderTop: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  inputRow: {
    display: "flex",
    gap: tokens.spacingHorizontalS,
    alignItems: "flex-end",
  },
  inputField: {
    flex: 1,
  },
  emptyState: {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: tokens.spacingVerticalXXL,
    color: tokens.colorNeutralForeground3,
  },
  emptyStateIcon: {
    fontSize: "48px",
    marginBottom: tokens.spacingVerticalM,
  },
  reportContainer: {
    flex: 1,
    padding: tokens.spacingVerticalL,
    paddingLeft: tokens.spacingHorizontalXXL,
    paddingRight: tokens.spacingHorizontalXXL,
    overflowY: "auto",
  },
  reportCard: {
    maxWidth: "800px",
    marginBottom: tokens.spacingVerticalL,
  },
  reportPlaceholder: {
    padding: tokens.spacingVerticalXXL,
    textAlign: "center",
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: "8px",
    border: `2px dashed ${tokens.colorNeutralStroke1}`,
  },
  reportForm: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacingVerticalM,
    marginBottom: tokens.spacingVerticalL,
    maxWidth: "100%",
  },
  reportFormRow: {
    display: "flex",
    alignItems: "flex-end",
    gap: tokens.spacingHorizontalL,
    flexWrap: "wrap",
  },
  reportDateField: {
    minWidth: "160px",
  },
  reportEmailField: {
    flex: 1,
    minWidth: "300px",
  },
  reportTable: {
    maxWidth: "100%",
    overflowX: "auto",
  },
  percentGreen: {
    backgroundColor: "#ccffcc",
    padding: "2px 8px",
    borderRadius: "4px",
    display: "inline-block",
  },
  percentYellow: {
    backgroundColor: "#ffffcc",
    padding: "2px 8px",
    borderRadius: "4px",
    display: "inline-block",
  },
  percentRed: {
    backgroundColor: "#ffcccc",
    padding: "2px 8px",
    borderRadius: "4px",
    display: "inline-block",
  },
  percentGrey: {
    backgroundColor: "#d0d0d0",
    padding: "2px 8px",
    borderRadius: "4px",
    display: "inline-block",
  },
  reportResults: {
    marginTop: tokens.spacingVerticalL,
  },
  reportSummary: {
    marginBottom: tokens.spacingVerticalM,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground3,
    borderRadius: "6px",
  },
  emailSuccess: {
    marginTop: tokens.spacingVerticalM,
  },
  daysSevere: {
    backgroundColor: "#ffcccc",
    padding: "2px 8px",
    borderRadius: "4px",
    display: "inline-block",
  },
  daysWarning: {
    backgroundColor: "#ffffcc",
    padding: "2px 8px",
    borderRadius: "4px",
    display: "inline-block",
  },
  daysMild: {
    backgroundColor: "#fff3cd",
    padding: "2px 8px",
    borderRadius: "4px",
    display: "inline-block",
  },
});

// ============================================================================
// Sub-Components
// ============================================================================

interface ChatPanelProps {
  api: ReturnType<typeof useCadApiClient>;
}

const ChatPanel: React.FC<ChatPanelProps> = ({ api }) => {
  const styles = useStyles();
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [currentConversation, setCurrentConversation] = useState<Conversation | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [pendingMessage, setPendingMessage] = useState<string | null>(null);
  const [streamingResponse, setStreamingResponse] = useState<string>("");
  const [streamingStatus, setStreamingStatus] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const [editingTitle, setEditingTitle] = useState<string | null>(null);
  const [editTitleValue, setEditTitleValue] = useState("");

  // Load conversations on mount
  useEffect(() => {
    loadConversations();
  }, []);

  // Scroll to bottom when messages change or streaming updates
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [currentConversation?.messages, streamingResponse, isSending]);

  const loadConversations = async () => {
    setIsLoading(true);
    try {
      const data = await api.get<ConversationSummary[]>("/api/conversations");
      setConversations(data || []);
    } catch (error) {
      console.error("Failed to load conversations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadConversation = async (id: string) => {
    setIsLoading(true);
    try {
      const data = await api.get<Conversation>(`/api/conversations/${id}`);
      setCurrentConversation(data);
    } catch (error) {
      console.error("Failed to load conversation:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const createNewConversation = async () => {
    setCurrentConversation(null);
    setInputValue("");
  };

  const sendMessage = async (messageOverride?: string) => {
    const messageContent = (messageOverride !== undefined ? messageOverride : inputValue).trim();
    if (!messageContent || isSending) return;
    setInputValue("");
    setIsSending(true);
    setPendingMessage(messageContent);
    setStreamingResponse("");
    setStreamingStatus(null);
    setErrorMessage(null);

    try {
      const baseUrl = api.getBaseUrl();
      let headers = await api.getAuthHeaders();

      let response = await fetch(`${baseUrl}/api/chat/send/stream`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          message: messageContent,
          conversation_id: currentConversation?.id,
        }),
      });

      if (response.status === 401) {
        headers = await api.getAuthHeaders(true);
        response = await fetch(`${baseUrl}/api/chat/send/stream`, {
          method: "POST",
          headers,
          body: JSON.stringify({
            message: messageContent,
            conversation_id: currentConversation?.id,
          }),
        });
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error("No response body");
      }

      const decoder = new TextDecoder();
      let conversationId: string | null = null;
      let fullResponse = "";

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        const chunk = decoder.decode(value, { stream: true });
        const lines = chunk.split("\n");

        for (const line of lines) {
          if (line.startsWith("data: ")) {
            const raw = line.slice(6).trim();
            if (!raw) continue;

            let data: any;
            try {
              data = JSON.parse(raw);
            } catch {
              console.warn("Failed to parse SSE data:", line);
              continue;
            }

            if (data.type === "init") {
              conversationId = data.conversation_id;
            } else if (data.type === "text") {
              fullResponse += data.content;
              setStreamingResponse(fullResponse);
            } else if (data.type === "status") {
              setStreamingStatus(data.content);
            } else if (data.type === "tool_start") {
              setStreamingStatus(`Executing query: ${data.query}...`);
            } else if (data.type === "tool_end") {
              setStreamingStatus(`Query returned ${data.rows} row(s)`);
            } else if (data.type === "done") {
              setStreamingStatus(null);
              if (conversationId) {
                await loadConversation(conversationId);
              }
              await loadConversations();
            } else if (data.type === "error") {
              throw new Error(data.content || "Server error");
            }
          }
        }
      }

      if (!fullResponse.trim()) {
        throw new Error("No response received. Your session may have expired — try refreshing the page.");
      }
    } catch (error: any) {
      console.error("Failed to send message:", error);
      const message = error?.message || "Failed to send message. Please try again.";
      setErrorMessage(message);
      setInputValue(messageContent);
      setTimeout(() => setErrorMessage(null), 10000);
    } finally {
      setIsSending(false);
      setPendingMessage(null);
      setStreamingResponse("");
      setStreamingStatus(null);
    }
  };

  const deleteConversation = async (id: string) => {
    try {
      await api.delete(`/api/conversations/${id}`);
      if (currentConversation?.id === id) {
        setCurrentConversation(null);
      }
      await loadConversations();
    } catch (error) {
      console.error("Failed to delete conversation:", error);
    }
  };

  const updateTitle = async (id: string, title: string) => {
    try {
      await api.patch(`/api/conversations/${id}`, { title });
      await loadConversations();
      if (currentConversation?.id === id) {
        setCurrentConversation({ ...currentConversation, title });
      }
    } catch (error) {
      console.error("Failed to update title:", error);
    }
    setEditingTitle(null);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  return (
    <div className={styles.chatContainer}>
      {/* Sidebar with conversation history */}
      <div className={styles.sidebar}>
        <div className={styles.sidebarHeader}>
          <Text weight="semibold">
            <History24Regular style={{ marginRight: 8, verticalAlign: "middle" }} />
            Conversations
          </Text>
          <Button
            appearance="subtle"
            icon={<Add24Regular />}
            onClick={createNewConversation}
            title="New conversation"
          />
        </div>
        <div className={styles.conversationList}>
          {isLoading && conversations.length === 0 ? (
            <Spinner size="small" label="Loading..." />
          ) : conversations.length === 0 ? (
            <Text size={200} style={{ padding: "16px", color: tokens.colorNeutralForeground3 }}>
              No conversations yet. Start a new one!
            </Text>
          ) : (
            conversations.map((conv) => (
              <div
                key={conv.id}
                className={`${styles.conversationItem} ${
                  currentConversation?.id === conv.id ? styles.conversationItemActive : ""
                }`}
                onClick={() => loadConversation(conv.id)}
              >
                <div className={styles.conversationInfo}>
                  {editingTitle === conv.id ? (
                    <Input
                      size="small"
                      value={editTitleValue}
                      onChange={(e, data) => setEditTitleValue(data.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          updateTitle(conv.id, editTitleValue);
                        } else if (e.key === "Escape") {
                          setEditingTitle(null);
                        }
                      }}
                      onClick={(e) => e.stopPropagation()}
                    />
                  ) : (
                    <>
                      <div className={styles.conversationTitle}>{conv.title}</div>
                      {conv.preview && (
                        <div className={styles.conversationPreview}>{conv.preview}</div>
                      )}
                    </>
                  )}
                </div>
                <Menu>
                  <MenuTrigger>
                    <Button
                      appearance="subtle"
                      size="small"
                      icon={<MoreHorizontal24Regular />}
                      onClick={(e) => e.stopPropagation()}
                    />
                  </MenuTrigger>
                  <MenuPopover>
                    <MenuList>
                      <MenuItem
                        icon={<Edit24Regular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingTitle(conv.id);
                          setEditTitleValue(conv.title);
                        }}
                      >
                        Rename
                      </MenuItem>
                      <MenuItem
                        icon={<Delete24Regular />}
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteConversation(conv.id);
                        }}
                      >
                        Delete
                      </MenuItem>
                    </MenuList>
                  </MenuPopover>
                </Menu>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Main chat area */}
      <div className={styles.mainChat}>
        {currentConversation ? (
          <>
            <div className={styles.messagesContainer}>
              {currentConversation.messages.map((msg, idx) => (
                <div
                  key={idx}
                  className={`${styles.message} ${
                    msg.role === "user" ? styles.messageUser : ""
                  }`}
                >
                  <div className={styles.messageLabel}>
                    {msg.role === "user" ? "You" : "CAD Assistant"}
                  </div>
                  <div
                    className={`${styles.messageBubble} ${
                      msg.role === "user"
                        ? styles.messageBubbleUser
                        : styles.messageBubbleAssistant
                    }`}
                  >
                    {msg.role === "user" ? msg.content : (
                      <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                    )}
                  </div>
                </div>
              ))}
              {isSending && pendingMessage && (
                <>
                  <div className={`${styles.message} ${styles.messageUser}`}>
                    <div className={styles.messageLabel}>You</div>
                    <div className={`${styles.messageBubble} ${styles.messageBubbleUser}`}>
                      {pendingMessage}
                    </div>
                  </div>
                  <div className={styles.message}>
                    <div className={styles.messageLabel}>CAD Assistant</div>
                    <div className={`${styles.messageBubble} ${styles.messageBubbleAssistant}`}>
                      {streamingResponse ? (
                        <>
                          <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingResponse}</ReactMarkdown>
                          {streamingStatus && (
                            <div style={{ 
                              marginTop: 8, 
                              padding: "4px 8px", 
                              backgroundColor: tokens.colorNeutralBackground3,
                              borderRadius: "4px",
                              fontSize: "12px",
                              color: tokens.colorNeutralForeground3,
                              display: "flex",
                              alignItems: "center",
                              gap: 8
                            }}>
                              <Spinner size="extra-tiny" />
                              {streamingStatus}
                            </div>
                          )}
                        </>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          <Spinner size="tiny" />
                          <span>{streamingStatus || "Thinking..."}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
              <div ref={messagesEndRef} />
            </div>
          </>
        ) : isSending ? (
          /* Show user's question and streaming response for new conversations */
          <div className={styles.messagesContainer}>
            {pendingMessage && (
              <div className={`${styles.message} ${styles.messageUser}`}>
                <div className={styles.messageLabel}>You</div>
                <div className={`${styles.messageBubble} ${styles.messageBubbleUser}`}>
                  {pendingMessage}
                </div>
              </div>
            )}
            <div className={styles.message}>
              <div className={styles.messageLabel}>CAD Assistant</div>
              <div className={`${styles.messageBubble} ${styles.messageBubbleAssistant}`}>
                {streamingResponse ? (
                  <>
                    <ReactMarkdown remarkPlugins={[remarkGfm]}>{streamingResponse}</ReactMarkdown>
                    {streamingStatus && (
                      <div style={{ 
                        marginTop: 8, 
                        padding: "4px 8px", 
                        backgroundColor: tokens.colorNeutralBackground3,
                        borderRadius: "4px",
                        fontSize: "12px",
                        color: tokens.colorNeutralForeground3,
                        display: "flex",
                        alignItems: "center",
                        gap: 8
                      }}>
                        <Spinner size="extra-tiny" />
                        {streamingStatus}
                      </div>
                    )}
                  </>
                ) : (
                  <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                    <Spinner size="tiny" />
                    <span>{streamingStatus || "Analyzing your question..."}</span>
                  </div>
                )}
              </div>
            </div>
            <div ref={messagesEndRef} />
          </div>
        ) : (
          <div className={styles.emptyState}>
            <Chat24Regular className={styles.emptyStateIcon} />
            <Text size={500} weight="semibold">
              CAD Analysis Assistant
            </Text>
            <Text size={300} style={{ marginTop: 8, maxWidth: 400, textAlign: "center" }}>
              Ask questions about CAD matching issues, data discrepancies, and get AI-powered
              suggestions for solutions.
            </Text>
          </div>
        )}

        {errorMessage && (
          <div style={{
            padding: "8px 16px",
            backgroundColor: tokens.colorPaletteRedBackground2,
            color: tokens.colorPaletteRedForeground1,
            borderRadius: "4px",
            margin: "0 16px 8px 16px",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}>
            <Text size={200}>{errorMessage}</Text>
            <Button
              appearance="subtle"
              size="small"
              icon={<Dismiss24Regular />}
              onClick={() => setErrorMessage(null)}
            />
          </div>
        )}

        <div className={styles.inputContainer}>
          <div className={styles.inputRow}>
            <Textarea
              className={styles.inputField}
              placeholder="Ask about CAD matching issues..."
              value={inputValue}
              onChange={(e, data) => setInputValue(data.value)}
              onKeyDown={handleKeyDown}
              disabled={isSending}
              rows={2}
              resize="vertical"
            />
            <VoiceTextInputButton
              value={inputValue}
              onChange={setInputValue}
              onDictationComplete={(text) => void sendMessage(text)}
              disabled={isSending}
            />
            <Button
              appearance="primary"
              icon={<Send24Regular />}
              onClick={() => void sendMessage()}
              disabled={!inputValue.trim() || isSending}
            >
              Send
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Placeholder report panels
const ReportPanel: React.FC<{
  title: string;
  description: string;
  icon: React.ReactNode;
}> = ({ title, description, icon }) => {
  const styles = useStyles();

  return (
    <div className={styles.reportContainer}>
      <Card className={styles.reportCard}>
        <CardHeader
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              {icon}
              <Text weight="semibold">{title}</Text>
            </div>
          }
          description={description}
        />
      </Card>
      <div className={styles.reportPlaceholder}>
        <Text size={400} weight="semibold" block style={{ marginBottom: 8 }}>
          Coming Soon
        </Text>
        <Text size={300}>
          This report will be available once the Python scripts are integrated.
        </Text>
      </div>
    </div>
  );
};

// ============================================================================
// CAD Matching Report Panel
// ============================================================================

interface CADMatchingReportPanelProps {
  api: ReturnType<typeof useCadApiClient>;
}

const CADMatchingReportPanel: React.FC<CADMatchingReportPanelProps> = ({ api }) => {
  const styles = useStyles();
  
  // Form state
  const [startDate, setStartDate] = useState<string>("");
  const [endDate, setEndDate] = useState<string>("");
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [emailAddresses, setEmailAddresses] = useState<string>("");
  
  // Report state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reportData, setReportData] = useState<CADMatchingReportData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Get percentage style class
  const getPercentClass = (pct: number | null): string => {
    if (pct === null) return styles.percentGrey;
    if (pct < 40) return styles.percentRed;
    if (pct < 80) return styles.percentYellow;
    return styles.percentGreen;
  };

  // Format percentage display
  const formatPercent = (pct: number | null): string => {
    if (pct === null) return "-";
    return `${pct.toFixed(1)}%`;
  };

  // Get CAD date style class based on recency
  const getCadDateClass = (dateStr: string | null): string => {
    if (!dateStr) return styles.percentRed; // "Never" - red
    
    const cadDate = new Date(dateStr);
    const now = new Date();
    const diffMs = now.getTime() - cadDate.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);
    const diffDays = diffHours / 24;
    
    if (diffHours <= 24) return styles.percentGreen;      // Within 24 hours - green
    if (diffDays <= 14) return styles.percentYellow;       // 24h to 2 weeks - yellow
    return styles.percentRed;                              // More than 2 weeks - red
  };

  // Run the report
  const runReport = async () => {
    setIsLoading(true);
    setError(null);
    setReportData(null);

    try {
      // Build request body
      const requestBody: {
        start_date?: string;
        end_date?: string;
        include_all_orgs: boolean;
        send_email: boolean;
        email_addresses?: string[];
      } = {
        include_all_orgs: !!(startDate || endDate), // Include all orgs for date-bounded reports
        send_email: sendEmail,
      };

      // Add date range if provided
      if (startDate) {
        requestBody.start_date = new Date(startDate).toISOString();
      }
      if (endDate) {
        // Set end date to end of day
        const endDateObj = new Date(endDate);
        endDateObj.setHours(23, 59, 59, 999);
        requestBody.end_date = endDateObj.toISOString();
      }

      // Add email addresses if sending email
      if (sendEmail && emailAddresses.trim()) {
        requestBody.email_addresses = emailAddresses
          .split(/[,;\s]+/)
          .map(e => e.trim())
          .filter(e => e.includes("@"));
      }

      const response = await api.post<CADMatchingReportData>(
        "/api/reports/cad-matching",
        requestBody
      );
      
      setReportData(response);
    } catch (err) {
      console.error("Failed to generate report:", err);
      setError(err instanceof Error ? err.message : "Failed to generate report");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.reportContainer}>
      <Card className={styles.reportCard}>
        <CardHeader
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <DocumentTable24Regular />
              <Text weight="semibold">CAD Matching Report</Text>
            </div>
          }
          description="View CAD matching statistics for all CAD-enabled organizations"
        />
      </Card>

      {/* Report Form */}
      <div className={styles.reportForm}>
        <Text weight="semibold" size={300}>
          Report Options
        </Text>
        
        <div className={styles.reportFormRow}>
          <Field 
            label="Start Date (optional)" 
            className={styles.reportDateField}
          >
            <Input
              type="date"
              value={startDate}
              onChange={(e, data) => setStartDate(data.value)}
            />
          </Field>
          
          <Field 
            label="End Date (optional)" 
            className={styles.reportDateField}
          >
            <Input
              type="date"
              value={endDate}
              onChange={(e, data) => setEndDate(data.value)}
            />
          </Field>
        </div>

        <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: -8 }}>
          Leave dates empty to show the default 60-day report with organizations below 80% threshold
        </Text>

        <Divider />

        <Checkbox
          checked={sendEmail}
          onChange={(e, data) => setSendEmail(data.checked === true)}
          label="Send report via email"
        />

        {sendEmail && (
          <Field 
            label="Email Addresses" 
            hint="Enter one or more email addresses, separated by commas"
            className={styles.reportEmailField}
          >
            <Input
              value={emailAddresses}
              onChange={(e, data) => setEmailAddresses(data.value)}
              placeholder="email1@example.com, email2@example.com"
            />
          </Field>
        )}

        <div>
          <Button
            appearance="primary"
            onClick={runReport}
            disabled={isLoading || (sendEmail && !emailAddresses.trim())}
          >
            {isLoading ? <Spinner size="tiny" /> : "Run Report"}
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalM }}>
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Email Confirmation - only show when email was actually requested */}
      {reportData?.email_sent === true && (
        <MessageBar 
          intent="success"
          className={styles.emailSuccess}
        >
          <MessageBarBody>
            <MessageBarTitle>Email Sent</MessageBarTitle>
            {reportData.email_message}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Report Results */}
      {reportData && (
        <div className={styles.reportResults}>
          <div className={styles.reportSummary}>
            <Text weight="semibold" block>
              Report Generated: {new Date(reportData.report_date).toLocaleString()}
            </Text>
            <Text size={200}>
              Date Range: {reportData.date_range.start.split("T")[0]} to{" "}
              {reportData.date_range.end.split("T")[0]} | 
              Organizations: {reportData.organization_count} | 
              Threshold: {reportData.threshold}%
            </Text>
          </div>

          {reportData.organizations.length === 0 ? (
            <div className={styles.reportPlaceholder}>
              <Text size={400} weight="semibold" block style={{ marginBottom: 8 }}>
                No Results
              </Text>
              <Text size={300}>
                No organizations with CAD matching issues found for the selected criteria.
              </Text>
            </div>
          ) : reportData.is_monitoring_report ? (
            /* Monitoring report - full columns */
            <div className={styles.reportTable}>
              <Table aria-label="CAD Matching Report" size="small">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>Organization</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center" }}>ID</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center", borderLeft: "2px solid #333" }}>Overall %</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center" }}>BWC %</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center", borderRight: "2px solid #333" }}>ICV %</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center", borderLeft: "2px solid #333" }}>14d BWC</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center", borderRight: "2px solid #333" }}>14d ICV</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center", borderLeft: "2px solid #333" }}>Yest BWC</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center", borderRight: "2px solid #333" }}>Yest ICV</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center" }}>Last CAD</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center" }}>Adjusted</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.organizations.map((org) => (
                    <TableRow key={org.organization_id}>
                      <TableCell>{org.organization_name}</TableCell>
                      <TableCell style={{ textAlign: "center" }}>{org.organization_id}</TableCell>
                      <TableCell style={{ textAlign: "center", borderLeft: "2px solid #333" }}>
                        <span className={getPercentClass(org.overall_percent)}>
                          {formatPercent(org.overall_percent)}
                        </span>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        <span className={getPercentClass(org.bwc_percent)}>
                          {formatPercent(org.bwc_percent)}
                        </span>
                      </TableCell>
                      <TableCell style={{ textAlign: "center", borderRight: "2px solid #333" }}>
                        <span className={getPercentClass(org.icv_percent)}>
                          {formatPercent(org.icv_percent)}
                        </span>
                      </TableCell>
                      <TableCell style={{ textAlign: "center", borderLeft: "2px solid #333" }}>
                        <span className={getPercentClass(org.fourteen_day_bwc_percent ?? null)}>
                          {formatPercent(org.fourteen_day_bwc_percent ?? null)}
                        </span>
                      </TableCell>
                      <TableCell style={{ textAlign: "center", borderRight: "2px solid #333" }}>
                        <span className={getPercentClass(org.fourteen_day_icv_percent ?? null)}>
                          {formatPercent(org.fourteen_day_icv_percent ?? null)}
                        </span>
                      </TableCell>
                      <TableCell style={{ textAlign: "center", borderLeft: "2px solid #333" }}>
                        <span className={getPercentClass(org.yesterday_bwc_percent ?? null)}>
                          {formatPercent(org.yesterday_bwc_percent ?? null)}
                        </span>
                      </TableCell>
                      <TableCell style={{ textAlign: "center", borderRight: "2px solid #333" }}>
                        <span className={getPercentClass(org.yesterday_icv_percent ?? null)}>
                          {formatPercent(org.yesterday_icv_percent ?? null)}
                        </span>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        <span className={getCadDateClass(org.last_cad_parsed)}>
                          {org.last_cad_parsed 
                            ? org.last_cad_parsed.split("T")[0] 
                            : "Never"}
                        </span>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        {org.window_adjusted || "No"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            /* Date range report - simplified columns */
            <div className={styles.reportTable}>
              <Table aria-label="CAD Matching Report">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell>Organization</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center" }}>ID</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center" }}>Overall %</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center" }}>BWC %</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center" }}>ICV %</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center" }}>Total</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center" }}>Matched</TableHeaderCell>
                    <TableHeaderCell style={{ textAlign: "center" }}>Last CAD</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {reportData.organizations.map((org) => (
                    <TableRow key={org.organization_id}>
                      <TableCell>{org.organization_name}</TableCell>
                      <TableCell style={{ textAlign: "center" }}>{org.organization_id}</TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        <span className={getPercentClass(org.overall_percent)}>
                          {formatPercent(org.overall_percent)}
                        </span>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        <span className={getPercentClass(org.bwc_percent)}>
                          {formatPercent(org.bwc_percent)}
                        </span>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        <span className={getPercentClass(org.icv_percent)}>
                          {formatPercent(org.icv_percent)}
                        </span>
                      </TableCell>
                      <TableCell style={{ textAlign: "center" }}>{org.total_videos}</TableCell>
                      <TableCell style={{ textAlign: "center" }}>{org.matched_videos}</TableCell>
                      <TableCell style={{ textAlign: "center" }}>
                        <span className={getCadDateClass(org.last_cad_parsed)}>
                          {org.last_cad_parsed 
                            ? org.last_cad_parsed.split("T")[0] 
                            : "Never"}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Excluded Organizations */}
          {reportData.excluded_organizations.length > 0 && (
            <div style={{ marginTop: tokens.spacingVerticalL }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                <em>
                  Excluded from report:{" "}
                  {reportData.excluded_organizations.map(o => o.name).join(", ")}
                </em>
              </Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Badge Mismatch Report Panel
// ============================================================================

// Status response from the badge mismatch status endpoint
interface BadgeMismatchStatus {
  has_previous_report: boolean;
  previous_report_date: string | null;
  is_running: boolean;
  running_since: string | null;
  running_job_id: string | null;
  email_when_done: boolean;
}

interface BadgeMismatchReportPanelProps {
  api: ReturnType<typeof useCadApiClient>;
}

const BadgeMismatchReportPanel: React.FC<BadgeMismatchReportPanelProps> = ({ api }) => {
  const styles = useStyles();
  
  // Status state
  const [status, setStatus] = useState<BadgeMismatchStatus | null>(null);
  const [statusLoading, setStatusLoading] = useState<boolean>(true);
  
  // Form state for new report
  const [lookbackDays, setLookbackDays] = useState<number>(30);
  const [emailWhenDone, setEmailWhenDone] = useState<boolean>(false);
  const [emailAddresses, setEmailAddresses] = useState<string>("");
  const [showNewReportForm, setShowNewReportForm] = useState<boolean>(false);
  
  // Report viewing state
  const [reportData, setReportData] = useState<BadgeMismatchReportData | null>(null);
  const [reportLoading, setReportLoading] = useState<boolean>(false);
  
  // Email existing report state
  const [showEmailDialog, setShowEmailDialog] = useState<boolean>(false);
  const [emailDialogAddresses, setEmailDialogAddresses] = useState<string>("");
  const [emailSending, setEmailSending] = useState<boolean>(false);
  
  // General state
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  // Fetch status on mount and poll when running
  const fetchStatus = useCallback(async () => {
    try {
      const response = await api.get<BadgeMismatchStatus>("/api/reports/badge-mismatch/status");
      setStatus(response);
      return response;
    } catch (err) {
      console.error("Failed to fetch status:", err);
      return null;
    }
  }, [api]);

  useEffect(() => {
    const loadStatus = async () => {
      setStatusLoading(true);
      await fetchStatus();
      setStatusLoading(false);
    };
    loadStatus();
  }, [fetchStatus]);

  // Poll for status updates when a report is running
  useEffect(() => {
    if (!status?.is_running) return;
    
    const interval = setInterval(async () => {
      const newStatus = await fetchStatus();
      // If report just finished, load it automatically
      if (newStatus && !newStatus.is_running && newStatus.has_previous_report) {
        loadLatestReport();
      }
    }, 10000); // Poll every 10 seconds
    
    return () => clearInterval(interval);
  }, [status?.is_running, fetchStatus]);

  // Start a new report
  const startNewReport = async () => {
    setError(null);
    setSuccessMessage(null);
    
    try {
      const requestBody: {
        lookback_days: number;
        email_when_done: boolean;
        email_addresses?: string[];
      } = {
        lookback_days: lookbackDays,
        email_when_done: emailWhenDone,
      };

      if (emailWhenDone && emailAddresses.trim()) {
        requestBody.email_addresses = emailAddresses
          .split(/[,;\s]+/)
          .map(e => e.trim())
          .filter(e => e.includes("@"));
      }

      const response = await api.post<{ job_id: string; message: string }>(
        "/api/reports/badge-mismatch/start",
        requestBody
      );
      
      setSuccessMessage(response.message);
      setShowNewReportForm(false);
      setReportData(null); // Clear any previous report view
      await fetchStatus(); // Refresh status to show running
    } catch (err) {
      console.error("Failed to start report:", err);
      setError(err instanceof Error ? err.message : "Failed to start report");
    }
  };

  // Load the latest report
  const loadLatestReport = async () => {
    setReportLoading(true);
    setError(null);
    
    try {
      const response = await api.get<BadgeMismatchReportData>("/api/reports/badge-mismatch/latest");
      setReportData(response);
    } catch (err) {
      console.error("Failed to load report:", err);
      setError(err instanceof Error ? err.message : "Failed to load report");
    } finally {
      setReportLoading(false);
    }
  };

  // Email the existing report
  const emailExistingReport = async () => {
    setEmailSending(true);
    setError(null);
    
    try {
      const addresses = emailDialogAddresses
        .split(/[,;\s]+/)
        .map(e => e.trim())
        .filter(e => e.includes("@"));
      
      await api.post("/api/reports/badge-mismatch/email-existing", {
        email_addresses: addresses,
      });
      
      setSuccessMessage(`Report emailed to ${addresses.join(", ")}`);
      setShowEmailDialog(false);
      setEmailDialogAddresses("");
    } catch (err) {
      console.error("Failed to email report:", err);
      setError(err instanceof Error ? err.message : "Failed to email report");
    } finally {
      setEmailSending(false);
    }
  };

  // Calculate running time (backend returns UTC ISO timestamp)
  const getRunningTime = () => {
    if (!status?.running_since) return "";
    // Parse the ISO string - it's in UTC
    const started = new Date(status.running_since);
    const now = new Date();
    // Both are in same timezone now, so diff works
    const totalSeconds = Math.floor((now.getTime() - started.getTime()) / 1000);
    // Handle negative values (clock skew) by showing 0
    const seconds = Math.max(0, totalSeconds);
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes}m ${secs}s`;
  };

  // Render a mismatch table section
  const renderMismatchTable = (
    records: BadgeMismatchRecord[],
    showCadBadge: boolean = true,
    showLastActivity: boolean = true
  ) => {
    // Group by organization
    const byOrg: Record<string, BadgeMismatchRecord[]> = {};
    for (const r of records) {
      const key = `${r.organization_name}|${r.organization_id}`;
      if (!byOrg[key]) byOrg[key] = [];
      byOrg[key].push(r);
    }

    return Object.entries(byOrg).map(([key, orgRecords]) => {
      const [orgName, orgIdStr] = key.split("|");
      return (
        <div key={key} style={{ marginBottom: tokens.spacingVerticalL }}>
          <Text weight="semibold" style={{ color: "#2E5090" }}>
            {orgName} (Org ID: {orgIdStr})
          </Text>
          <Table aria-label={`Mismatches for ${orgName}`} size="small" style={{ marginTop: 8 }}>
            <TableHeader>
              <TableRow>
                <TableHeaderCell>Officer Name</TableHeaderCell>
                {showCadBadge && <TableHeaderCell style={{ textAlign: "center" }}>CAD Badge</TableHeaderCell>}
                {!showCadBadge && <TableHeaderCell>CAD Name</TableHeaderCell>}
                <TableHeaderCell style={{ textAlign: "center" }}>Portal Badge</TableHeaderCell>
                {showLastActivity && <TableHeaderCell style={{ textAlign: "center" }}>Last CAD Activity</TableHeaderCell>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {orgRecords.map((r, idx) => {
                const name = `${r.found_first_name || ""} ${r.found_last_name || ""}`.trim() || r.officer_name;
                return (
                  <TableRow key={idx}>
                    <TableCell>{name}</TableCell>
                    {showCadBadge && <TableCell style={{ textAlign: "center" }}>{r.cad_badge || "-"}</TableCell>}
                    {!showCadBadge && <TableCell>{r.officer_name}</TableCell>}
                    <TableCell style={{ textAlign: "center" }}>{r.portal_badge || "-"}</TableCell>
                    {showLastActivity && (
                      <TableCell style={{ textAlign: "center" }}>
                        {r.last_cad_activity ? r.last_cad_activity.split("T")[0] : "-"}
                      </TableCell>
                    )}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      );
    });
  };

  if (statusLoading) {
    return (
      <div className={styles.reportContainer}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 20 }}>
          <Spinner size="small" />
          <Text>Loading report status...</Text>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.reportContainer}>
      <Card className={styles.reportCard}>
        <CardHeader
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <PersonTag24Regular />
              <Text weight="semibold">Badge # Mismatch Report</Text>
            </div>
          }
          description="Find badge number discrepancies between CAD data and portal users"
        />
      </Card>

      {/* Status Panel */}
      <div className={styles.reportForm}>
        <Text weight="semibold" size={300}>Report Status</Text>
        
        {status?.is_running ? (
          <MessageBar intent="warning" style={{ marginTop: 8 }}>
            <MessageBarBody>
              <MessageBarTitle>Report Generation In Progress</MessageBarTitle>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Spinner size="tiny" />
                <Text>Running for {getRunningTime()}... This may take 10-15 minutes.</Text>
              </div>
              {status.email_when_done && (
                <Text size={200} style={{ marginTop: 4 }}>
                  Email will be sent when complete.
                </Text>
              )}
            </MessageBarBody>
          </MessageBar>
        ) : status?.has_previous_report ? (
          <div style={{ 
            backgroundColor: tokens.colorNeutralBackground3, 
            padding: tokens.spacingVerticalM, 
            borderRadius: "4px",
            marginTop: 8 
          }}>
            <Text weight="semibold" block>
              Last Report: {status.previous_report_date ? new Date(status.previous_report_date).toLocaleString() : "Unknown"}
            </Text>
          </div>
        ) : (
          <div style={{ 
            backgroundColor: tokens.colorNeutralBackground3, 
            padding: tokens.spacingVerticalM, 
            borderRadius: "4px",
            marginTop: 8 
          }}>
            <Text>No reports have been generated yet.</Text>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: "flex", gap: 8, marginTop: tokens.spacingVerticalM, flexWrap: "wrap" }}>
          <Button
            appearance="primary"
            onClick={() => setShowNewReportForm(!showNewReportForm)}
            disabled={status?.is_running}
          >
            {showNewReportForm ? "Cancel" : "Generate New Report"}
          </Button>
          
          {status?.has_previous_report && !status.is_running && (
            <>
              <Button
                appearance="secondary"
                onClick={loadLatestReport}
                disabled={reportLoading}
              >
                {reportLoading ? <Spinner size="tiny" /> : "View Report"}
              </Button>
              <Button
                appearance="secondary"
                onClick={() => setShowEmailDialog(true)}
              >
                Email Report
              </Button>
            </>
          )}
        </div>
      </div>

      {/* New Report Form */}
      {showNewReportForm && (
        <div className={styles.reportForm} style={{ marginTop: tokens.spacingVerticalM }}>
          <Text weight="semibold" size={300}>New Report Options</Text>
          
          <div className={styles.reportFormRow}>
            <Field label="Lookback Period (days)" className={styles.reportDateField}>
              <Input
                type="number"
                value={lookbackDays.toString()}
                onChange={(e, data) => setLookbackDays(parseInt(data.value) || 30)}
                min={1}
                max={365}
              />
            </Field>
          </div>

          <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: -8 }}>
            How far back to look in CAD data for badge numbers (default: 30 days)
          </Text>

          <Divider />

          <Checkbox
            checked={emailWhenDone}
            onChange={(e, data) => setEmailWhenDone(data.checked === true)}
            label="Email report when complete"
          />

          {emailWhenDone && (
            <Field 
              label="Email Addresses" 
              hint="Enter one or more email addresses, separated by commas"
              className={styles.reportEmailField}
            >
              <Input
                value={emailAddresses}
                onChange={(e, data) => setEmailAddresses(data.value)}
                placeholder="email1@example.com, email2@example.com"
              />
            </Field>
          )}

          <MessageBar intent="info" style={{ marginTop: 8 }}>
            <MessageBarBody>
              This report takes approximately 10-15 minutes to generate. You can leave this page and come back later to view the results.
            </MessageBarBody>
          </MessageBar>

          <div style={{ marginTop: tokens.spacingVerticalM }}>
            <Button
              appearance="primary"
              onClick={startNewReport}
              disabled={emailWhenDone && !emailAddresses.trim()}
            >
              Start Report Generation
            </Button>
          </div>
        </div>
      )}

      {/* Email Dialog */}
      {showEmailDialog && (
        <div className={styles.reportForm} style={{ marginTop: tokens.spacingVerticalM }}>
          <Text weight="semibold" size={300}>Email Existing Report</Text>
          
          <Field 
            label="Email Addresses" 
            hint="Enter one or more email addresses, separated by commas"
            className={styles.reportEmailField}
          >
            <Input
              value={emailDialogAddresses}
              onChange={(e, data) => setEmailDialogAddresses(data.value)}
              placeholder="email1@example.com, email2@example.com"
            />
          </Field>

          <div style={{ display: "flex", gap: 8, marginTop: tokens.spacingVerticalM }}>
            <Button
              appearance="primary"
              onClick={emailExistingReport}
              disabled={emailSending || !emailDialogAddresses.trim()}
            >
              {emailSending ? <Spinner size="tiny" /> : "Send Email"}
            </Button>
            <Button
              appearance="secondary"
              onClick={() => { setShowEmailDialog(false); setEmailDialogAddresses(""); }}
            >
              Cancel
            </Button>
          </div>
        </div>
      )}

      {/* Error Message */}
      {error && (
        <MessageBar intent="error" style={{ marginTop: tokens.spacingVerticalM }}>
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Success Message */}
      {successMessage && (
        <MessageBar intent="success" style={{ marginTop: tokens.spacingVerticalM }}>
          <MessageBarBody>
            <MessageBarTitle>Success</MessageBarTitle>
            {successMessage}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Report Results */}
      {reportData && !showNewReportForm && (
        <div className={styles.reportResults}>
          {/* Summary */}
          <div className={styles.reportSummary}>
            <Text weight="semibold" block>
              Report Generated: {new Date(reportData.report_date).toLocaleString()}
            </Text>
            <Text size={200}>
              Lookback Period: {reportData.lookback_days} days | 
              Total Records: {reportData.summary.total_records}
            </Text>
          </div>

          {/* No results */}
          {reportData.summary.total_records === 0 ? (
            <div className={styles.reportPlaceholder}>
              <Text size={400} weight="semibold" block style={{ marginBottom: 8 }}>
                No Mismatches Found
              </Text>
              <Text size={300}>
                No badge number discrepancies were found in the last {reportData.lookback_days} days.
              </Text>
            </div>
          ) : (
            <>
              {/* Section 1: Portal Badge Mismatches */}
              {reportData.portal_mismatches.length > 0 && (
                <div style={{ marginBottom: tokens.spacingVerticalXL }}>
                  <Text size={400} weight="semibold" block style={{ marginBottom: 8, borderBottom: "2px solid #333", paddingBottom: 4 }}>
                    Portal Badge Mismatches ({reportData.summary.portal_mismatch_count})
                  </Text>
                  <Text size={200} block style={{ marginBottom: tokens.spacingVerticalM, color: tokens.colorNeutralForeground3 }}>
                    Officers matched by name but with different badge numbers. <strong>Action:</strong> Update badge in CAD or Portal to match.
                  </Text>
                  {renderMismatchTable(reportData.portal_mismatches)}
                </div>
              )}

              {/* Section 2: CAD Data Issues */}
              {reportData.cad_data_issues.length > 0 && (
                <div style={{ marginBottom: tokens.spacingVerticalXL }}>
                  <MessageBar intent="warning" style={{ marginBottom: tokens.spacingVerticalM }}>
                    <MessageBarBody>
                      <MessageBarTitle>CAD DATA SOURCE ISSUES - Missing Badge Numbers in CAD ({reportData.summary.cad_data_issue_count})</MessageBarTitle>
                      These officers exist in the portal but CAD is sending their data WITHOUT a badge number. This is a CAD configuration issue.
                    </MessageBarBody>
                  </MessageBar>
                  {renderMismatchTable(reportData.cad_data_issues, false, false)}
                </div>
              )}

              {/* Section 3: Disabled Users with Recent Activity */}
              {reportData.disabled_recent_activity.length > 0 && (
                <div style={{ marginBottom: tokens.spacingVerticalXL }}>
                  <MessageBar intent="warning" style={{ marginBottom: tokens.spacingVerticalM }}>
                    <MessageBarBody>
                      <MessageBarTitle>Disabled Portal Users with Recent CAD Activity ({reportData.summary.disabled_recent_count})</MessageBarTitle>
                      These officers are DISABLED in the portal but have recent CAD activity (within 7 days). They may need to be re-enabled.
                    </MessageBarBody>
                  </MessageBar>
                  {renderMismatchTable(reportData.disabled_recent_activity)}
                </div>
              )}

              {/* Section 4: Not Found Summary */}
              {reportData.summary.not_found_count > 0 && (
                <div style={{ marginBottom: tokens.spacingVerticalXL }}>
                  <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
                    <em>
                      Additionally, {reportData.summary.not_found_count} officers were found in CAD data that could not be matched to any portal user.
                    </em>
                  </Text>
                </div>
              )}

              {/* Summary Box */}
              <div style={{ 
                backgroundColor: "#E8F4FD", 
                padding: tokens.spacingVerticalM, 
                borderRadius: "5px",
                marginTop: tokens.spacingVerticalL 
              }}>
                <Text weight="semibold" block style={{ marginBottom: 8 }}>Summary</Text>
                {reportData.summary.portal_mismatch_count > 0 && (
                  <Text size={200} block>• <strong>{reportData.summary.portal_mismatch_count}</strong> Portal Badge Mismatches (enabled users, need correction)</Text>
                )}
                {reportData.summary.cad_data_issue_count > 0 && (
                  <Text size={200} block>• <strong>{reportData.summary.cad_data_issue_count}</strong> CAD Data Issues (missing badge in CAD data)</Text>
                )}
                {reportData.summary.disabled_recent_count > 0 && (
                  <Text size={200} block>• <strong>{reportData.summary.disabled_recent_count}</strong> Disabled users with recent CAD activity</Text>
                )}
                <Text size={200} block>• <strong>{reportData.summary.not_found_count}</strong> officers NOT FOUND in portal</Text>
                <Text size={200} block>• <strong>{reportData.summary.total_records}</strong> total records</Text>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Unit/Vehicle Report Panel (synchronous - 1-2 minutes)
// ============================================================================

interface UnitVehicleReportPanelProps {
  api: ReturnType<typeof useCadApiClient>;
}

const UnitVehicleReportPanel: React.FC<UnitVehicleReportPanelProps> = ({ api }) => {
  const styles = useStyles();
  
  // Form state
  const [lookbackDays, setLookbackDays] = useState<number>(30);
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [emailAddresses, setEmailAddresses] = useState<string>("");
  
  // Report state
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reportData, setReportData] = useState<UnitVehicleReportData | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Run the report
  const runReport = async () => {
    setIsLoading(true);
    setError(null);
    setReportData(null);

    try {
      const requestBody: {
        lookback_days: number;
        send_email: boolean;
        email_addresses?: string[];
      } = {
        lookback_days: lookbackDays,
        send_email: sendEmail,
      };

      if (sendEmail && emailAddresses.trim()) {
        requestBody.email_addresses = emailAddresses
          .split(/[,;\s]+/)
          .map(e => e.trim())
          .filter(e => e.includes("@"));
      }

      const response = await api.post<UnitVehicleReportData>(
        "/api/reports/unit-vehicle",
        requestBody
      );
      
      setReportData(response);
    } catch (err) {
      console.error("Failed to generate report:", err);
      setError(err instanceof Error ? err.message : "Failed to generate report");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.reportContainer}>
      <Card className={styles.reportCard}>
        <CardHeader
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <VehicleCar24Regular />
              <Text weight="semibold">Unit/Vehicle Mismatch Report</Text>
            </div>
          }
          description="Find unit and vehicle numbers in CAD data that don't match the Vehicle table"
        />
      </Card>

      {/* Report Form */}
      <div className={styles.reportForm}>
        <Text weight="semibold" size={300}>
          Report Options
        </Text>
        
        <div className={styles.reportFormRow}>
          <Field 
            label="Lookback Period (days)" 
            className={styles.reportDateField}
          >
            <Input
              type="number"
              value={lookbackDays.toString()}
              onChange={(e, data) => setLookbackDays(parseInt(data.value) || 30)}
              min={1}
              max={365}
            />
          </Field>
        </div>

        <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: -8 }}>
          How far back to look in CAD data (default: 30 days)
        </Text>

        <Divider />

        <Checkbox
          checked={sendEmail}
          onChange={(e, data) => setSendEmail(data.checked === true)}
          label="Send report via email"
        />

        {sendEmail && (
          <Field 
            label="Email Addresses" 
            hint="Enter one or more email addresses, separated by commas"
            className={styles.reportEmailField}
          >
            <Input
              value={emailAddresses}
              onChange={(e, data) => setEmailAddresses(data.value)}
              placeholder="email1@example.com, email2@example.com"
            />
          </Field>
        )}

        <MessageBar intent="info" style={{ marginTop: 8 }}>
          <MessageBarBody>
            This report takes 1-2 minutes to generate. Please wait after clicking Run Report.
          </MessageBarBody>
        </MessageBar>

        <div style={{ marginTop: tokens.spacingVerticalM }}>
          <Button
            appearance="primary"
            onClick={runReport}
            disabled={isLoading || (sendEmail && !emailAddresses.trim())}
          >
            {isLoading ? (
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <Spinner size="tiny" />
                <span>Generating report...</span>
              </div>
            ) : "Run Report"}
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <MessageBar intent="error" style={{ marginTop: tokens.spacingVerticalM }}>
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Email Confirmation */}
      {reportData?.email_sent === true && (
        <MessageBar 
          intent="success"
          className={styles.emailSuccess}
        >
          <MessageBarBody>
            <MessageBarTitle>Email Sent</MessageBarTitle>
            {reportData.email_message}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Report Results */}
      {reportData && (
        <div className={styles.reportResults}>
          {/* Summary */}
          <div className={styles.reportSummary}>
            <Text weight="semibold" block>
              Report Generated: {new Date(reportData.report_date).toLocaleString()}
            </Text>
            <Text size={200}>
              Lookback Period: {reportData.lookback_days} days | 
              Organizations: {reportData.summary.organization_count}
            </Text>
          </div>

          {/* Summary Stats */}
          <div style={{ 
            backgroundColor: "#E8F4FD", 
            padding: tokens.spacingVerticalM, 
            borderRadius: "5px",
            marginBottom: tokens.spacingVerticalL
          }}>
            <Text weight="semibold" block style={{ marginBottom: 8 }}>Summary</Text>
            <Text size={200} block style={{ color: "red" }}>
              Unmatched: {reportData.summary.total_unmatched} ({reportData.summary.unmatched_units} units, {reportData.summary.unmatched_vehicles} vehicles)
            </Text>
            <Text size={200} block style={{ color: "green" }}>
              Matched: {reportData.summary.total_matched} ({reportData.summary.matched_units} units, {reportData.summary.matched_vehicles} vehicles)
            </Text>
          </div>

          {/* No results */}
          {reportData.organizations.length === 0 ? (
            <div className={styles.reportPlaceholder}>
              <Text size={400} weight="semibold" block style={{ marginBottom: 8 }}>
                No Data Found
              </Text>
              <Text size={300}>
                No unit/vehicle data was found in CAD data from the last {reportData.lookback_days} days.
              </Text>
            </div>
          ) : (
            <div>
              {reportData.organizations.map((org) => (
                <div key={org.organization_id} style={{ marginBottom: tokens.spacingVerticalL }}>
                  <Text weight="semibold" style={{ color: "#2E5090" }}>
                    {org.organization_name} (Org ID: {org.organization_id})
                  </Text>
                  <div style={{ marginLeft: 16, marginTop: 4 }}>
                    {org.unmatched_vehicles.length > 0 && (
                      <Text size={200} block style={{ color: "red" }}>
                        Unmatched VehicleNumber(s): {org.unmatched_vehicles.join(", ")}
                      </Text>
                    )}
                    {org.unmatched_units.length > 0 && (
                      <Text size={200} block style={{ color: "red" }}>
                        Unmatched UnitNumber(s): {org.unmatched_units.join(", ")}
                      </Text>
                    )}
                    {org.matched_vehicles.length > 0 && (
                      <Text size={200} block style={{ color: "green" }}>
                        Matched VehicleNumber(s): {org.matched_vehicles.join(", ")}
                      </Text>
                    )}
                    {org.matched_units.length > 0 && (
                      <Text size={200} block style={{ color: "green" }}>
                        Matched UnitNumber(s): {org.matched_units.join(", ")}
                      </Text>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// Tardy Ingestion Report Panel
// ============================================================================

interface TardyIngestionReportPanelProps {
  api: ReturnType<typeof useCadApiClient>;
}

const TardyIngestionReportPanel: React.FC<TardyIngestionReportPanelProps> = ({ api }) => {
  const styles = useStyles();

  const [thresholdDays, setThresholdDays] = useState<number>(180);
  const [minDaysBehind, setMinDaysBehind] = useState<number>(2);
  const [sendEmail, setSendEmail] = useState<boolean>(false);
  const [emailAddresses, setEmailAddresses] = useState<string>("");

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [reportData, setReportData] = useState<TardyIngestionReportData | null>(null);
  const [error, setError] = useState<string | null>(null);

  const getDaysClass = (days: number): string => {
    if (days >= 14) return styles.daysSevere;
    if (days >= 7) return styles.daysWarning;
    return styles.daysMild;
  };

  const runReport = async () => {
    setIsLoading(true);
    setError(null);
    setReportData(null);

    try {
      const requestBody: {
        threshold_days: number;
        min_days_behind: number;
        send_email: boolean;
        email_addresses?: string[];
      } = {
        threshold_days: thresholdDays,
        min_days_behind: minDaysBehind,
        send_email: sendEmail,
      };

      if (sendEmail && emailAddresses.trim()) {
        requestBody.email_addresses = emailAddresses
          .split(/[,;\s]+/)
          .map(e => e.trim())
          .filter(e => e.includes("@"));
      }

      const response = await api.post<TardyIngestionReportData>(
        "/api/reports/tardy-ingestion",
        requestBody
      );

      setReportData(response);
    } catch (err) {
      console.error("Failed to generate report:", err);
      setError(err instanceof Error ? err.message : "Failed to generate report");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className={styles.reportContainer}>
      <Card className={styles.reportCard}>
        <CardHeader
          header={
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <Clock24Regular />
              <Text weight="semibold">CAD Tardy Ingestions</Text>
            </div>
          }
          description="Identify organizations whose CAD data file ingestion is behind schedule"
        />
      </Card>

      {/* Report Form */}
      <div className={styles.reportForm}>
        <Text weight="semibold" size={300}>
          Report Options
        </Text>

        <div className={styles.reportFormRow}>
          <Field
            label="Activity Window (days)"
            className={styles.reportDateField}
          >
            <Input
              type="number"
              value={thresholdDays.toString()}
              onChange={(e, data) => setThresholdDays(parseInt(data.value) || 180)}
              min={30}
              max={365}
            />
          </Field>

          <Field
            label="Min Days Behind"
            className={styles.reportDateField}
          >
            <Input
              type="number"
              value={minDaysBehind.toString()}
              onChange={(e, data) => setMinDaysBehind(parseInt(data.value) || 2)}
              min={1}
              max={90}
            />
          </Field>
        </div>

        <Text size={200} style={{ color: tokens.colorNeutralForeground3, marginTop: -8 }}>
          Only organizations active within the activity window and more than the minimum days behind are shown
        </Text>

        <Divider />

        <Checkbox
          checked={sendEmail}
          onChange={(e, data) => setSendEmail(data.checked === true)}
          label="Send report via email"
        />

        {sendEmail && (
          <Field
            label="Email Addresses"
            hint="Enter one or more email addresses, separated by commas"
            className={styles.reportEmailField}
          >
            <Input
              value={emailAddresses}
              onChange={(e, data) => setEmailAddresses(data.value)}
              placeholder="email1@example.com, email2@example.com"
            />
          </Field>
        )}

        <div>
          <Button
            appearance="primary"
            onClick={runReport}
            disabled={isLoading || (sendEmail && !emailAddresses.trim())}
          >
            {isLoading ? <Spinner size="tiny" /> : "Run Report"}
          </Button>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <MessageBar intent="error" style={{ marginBottom: tokens.spacingVerticalM }}>
          <MessageBarBody>
            <MessageBarTitle>Error</MessageBarTitle>
            {error}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Email Confirmation */}
      {reportData?.email_sent === true && (
        <MessageBar
          intent="success"
          className={styles.emailSuccess}
        >
          <MessageBarBody>
            <MessageBarTitle>Email Sent</MessageBarTitle>
            {reportData.email_message}
          </MessageBarBody>
        </MessageBar>
      )}

      {/* Report Results */}
      {reportData && (
        <div className={styles.reportResults}>
          <div className={styles.reportSummary}>
            <Text weight="semibold" block>
              Report Generated: {new Date(reportData.report_date).toLocaleString()}
            </Text>
            <Text size={200}>
              Activity Window: {reportData.threshold_days} days |
              Min Days Behind: {reportData.min_days_behind} |
              Organizations: {reportData.organization_count}
            </Text>
          </div>

          {reportData.all_well ? (
            <MessageBar intent="success" style={{ marginTop: tokens.spacingVerticalM }}>
              <MessageBarBody>
                <MessageBarTitle>All Systems Normal</MessageBarTitle>
                No organizations are more than {reportData.min_days_behind} days behind.
                All CAD ingestions are up to date.
              </MessageBarBody>
            </MessageBar>
          ) : (
            <div className={styles.reportTable}>
              <Table aria-label="Tardy Ingestion Report" size="small" style={{ tableLayout: "fixed" }}>
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell style={{ width: "50%" }}>Organization</TableHeaderCell>
                    <TableHeaderCell style={{ width: "25%", textAlign: "center" }}>Most Recent Parsed Date</TableHeaderCell>
                    <TableHeaderCell style={{ width: "25%", textAlign: "center" }}>Days Behind</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[...reportData.organizations]
                    .sort((a, b) => a.days_behind - b.days_behind)
                    .map((org) => (
                    <TableRow key={org.organization_id}>
                      <TableCell style={{ width: "50%" }}>{org.organization_name}</TableCell>
                      <TableCell style={{ width: "25%", textAlign: "center" }}>
                        {org.most_recent_parsed_date}
                      </TableCell>
                      <TableCell style={{ width: "25%", textAlign: "center" }}>
                        <span className={getDaysClass(org.days_behind)}>
                          {org.days_behind}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}

          {/* Excluded Organizations */}
          {reportData.excluded_organizations.length > 0 && (
            <div style={{ marginTop: tokens.spacingVerticalL }}>
              <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                <em>
                  Excluded from monitoring:{" "}
                  {reportData.excluded_organizations.map(o => o.name).join(", ")}
                </em>
              </Text>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ============================================================================
// No Permission Panel
// ============================================================================

const NoPermissionPanel: React.FC<{ feature: string }> = ({ feature }) => {
  const styles = useStyles();
  return (
    <div className={styles.reportContainer}>
      <div className={styles.reportPlaceholder}>
        <Text size={400} weight="semibold" block style={{ marginBottom: 8 }}>
          Access Restricted
        </Text>
        <Text size={300}>
          You don't have permission to access {feature}. Contact your administrator to request access.
        </Text>
      </div>
    </div>
  );
};

// ============================================================================
// Main Component
// ============================================================================

export const CADAnalysis: React.FC = () => {
  const styles = useStyles();
  const api = useCadApiClient();
  const { trackSubtoolAccess, trackEvent } = useUsageTracking();
  const [selectedTab, setSelectedTab] = useState<TabValue>("chat");
  const [permissions, setPermissions] = useState<CADPermissions | null>(null);
  const [permissionsLoading, setPermissionsLoading] = useState(true);

  // Fetch permissions on mount
  useEffect(() => {
    const loadPermissions = async () => {
      try {
        const perms = await api.get<CADPermissions>("/permissions");
        setPermissions(perms);
        
        // Select first available tab if current is not allowed
        if (perms && !perms.features.chat && selectedTab === "chat") {
          if (perms.features.cad_matching) setSelectedTab("cad-matching");
          else if (perms.features.badge_mismatch) setSelectedTab("badge-mismatch");
          else if (perms.features.unit_vehicle) setSelectedTab("unit-vehicle");
          else if (perms.features.tardy_ingestion) setSelectedTab("tardy-ingestion");
          else if (perms.features.cad_catchup) setSelectedTab("cad-catchup");
        }
      } catch (error) {
        console.error("Failed to load permissions:", error);
        // In dev mode, grant all permissions
        setPermissions({
          user_id: "dev",
          tool_id: "cad-analysis",
          permissions: ["access", "chat", "cad-matching", "badge-mismatch", "unit-vehicle", "cad-catchup", "tardy-ingestion"],
          features: {
            chat: true,
            cad_matching: true,
            badge_mismatch: true,
            unit_vehicle: true,
            cad_catchup: true,
            tardy_ingestion: true,
          },
        });
      } finally {
        setPermissionsLoading(false);
      }
    };
    loadPermissions();
  }, [api]);

  const handleTabSelect = (event: SelectTabEvent, data: SelectTabData) => {
    setSelectedTab(data.value);
    trackSubtoolAccess("cad-analysis", String(data.value));
  };

  // Track initial tab on mount
  useEffect(() => {
    if (!permissionsLoading) {
      trackSubtoolAccess("cad-analysis", String(selectedTab));
    }
  }, [permissionsLoading]); // Only on initial load

  if (permissionsLoading) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <Spinner size="large" label="Loading permissions..." />
        </div>
      </div>
    );
  }

  const features = permissions?.features || {
    chat: false,
    cad_matching: false,
    badge_mismatch: false,
    unit_vehicle: false,
    cad_catchup: false,
    tardy_ingestion: false,
  };

  return (
    <div className={styles.container}>
      {/* Header */}
      <div className={styles.header}>
        <div className={styles.headerTitle}>
          <div className={styles.headerIcon}>
            <DocumentTable24Regular />
          </div>
          <div>
            <Text size={500} weight="semibold">
              Portal & CAD Agent
            </Text>
            <Text size={200} style={{ display: "block", color: tokens.colorNeutralForeground3 }}>
              AI-powered portal analysis and reports
            </Text>
          </div>
        </div>
      </div>

      {/* Tab Navigation */}
      <div className={styles.tabList}>
        <TabList selectedValue={selectedTab} onTabSelect={handleTabSelect}>
          <Tab 
            value="chat" 
            icon={<Chat24Regular />}
            disabled={!features.chat}
          >
            AI Assistant
          </Tab>
          <Tab 
            value="cad-matching" 
            icon={<DocumentTable24Regular />}
            disabled={!features.cad_matching}
          >
            CAD Matching Report
          </Tab>
          <Tab 
            value="badge-mismatch" 
            icon={<PersonTag24Regular />}
            disabled={!features.badge_mismatch}
          >
            Badge # Mismatch
          </Tab>
          <Tab 
            value="unit-vehicle" 
            icon={<VehicleCar24Regular />}
            disabled={!features.unit_vehicle}
          >
            Unit/Vehicle Mismatch
          </Tab>
          <Tab
            value="tardy-ingestion"
            icon={<Clock24Regular />}
            disabled={!features.tardy_ingestion}
          >
            CAD Tardy Ingestions
          </Tab>
          <Tab 
            value="cad-catchup" 
            icon={<ArrowSync24Regular />}
            disabled={!features.cad_catchup}
          >
            CAD Catchup
          </Tab>
        </TabList>
      </div>

      {/* Content Area */}
      <div className={styles.content}>
        {selectedTab === "chat" && (
          features.chat ? (
            <ChatPanel api={api} />
          ) : (
            <NoPermissionPanel feature="the AI Assistant" />
          )
        )}
        {selectedTab === "cad-matching" && (
          features.cad_matching ? (
            <CADMatchingReportPanel api={api} />
          ) : (
            <NoPermissionPanel feature="CAD Matching Reports" />
          )
        )}
        {selectedTab === "badge-mismatch" && (
          features.badge_mismatch ? (
            <BadgeMismatchReportPanel api={api} />
          ) : (
            <NoPermissionPanel feature="Badge Mismatch Reports" />
          )
        )}
        {selectedTab === "unit-vehicle" && (
          features.unit_vehicle ? (
            <UnitVehicleReportPanel api={api} />
          ) : (
            <NoPermissionPanel feature="Unit/Vehicle Reports" />
          )
        )}
        {selectedTab === "tardy-ingestion" && (
          features.tardy_ingestion ? (
            <TardyIngestionReportPanel api={api} />
          ) : (
            <NoPermissionPanel feature="CAD Tardy Ingestion Reports" />
          )
        )}
        {selectedTab === "cad-catchup" && (
          features.cad_catchup ? (
            <ReportPanel
              title="CAD Catchup"
              description="Re-process videos that may have missed CAD matching"
              icon={<ArrowSync24Regular />}
            />
          ) : (
            <NoPermissionPanel feature="CAD Catchup" />
          )
        )}
      </div>
    </div>
  );
};

export default CADAnalysis;
